import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";

const P = {
  bg:"#080B14", card:"#0D1020", border:"#1D2238",
  gold:"#E9622A", goldGlow:"rgba(233,98,42,0.12)", goldBorder:"rgba(233,98,42,0.30)",
  text:"#EBE8F5", muted:"#8A8EAE", secondary:"#B4B7D0",
  green:"#5BB87A", yellow:"#E9622A", red:"#C46B6B", blue:"#5A82B8", purple:"#a78bfa",
  sidebar:"#060810", card2:"#111525", card3:"#161B2E",
  borderSubtle:"#141829", borderStrong:"#2A3055"
};

const DAYS_IT_CAL = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
const MONTHS_IT_CAL = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

export default function Calendario({
  calDate, setCalDate,
  calView, setCalView,
  calRecurring, setCalRecurring,
  calSingle, setCalSingle,
  calFiltroMaestro, setCalFiltroMaestro,
  calHidden, setCalHidden,
  maestriColors, maestriDB,
  saveEventoToDB, notify,
  readonlyMaestro = null, // se non null: ruolo maestro — può solo aggiungere i propri eventi
  isMaestroRole = false,  // true se l'utente ha ruolo maestro
}) {
  const [localDate, setLocalDate] = useState(calDate);
  const [localView, setLocalView] = useState(calView);
  const [modal, setModal] = useState(null);
  const [showAule, setShowAule] = useState(false);
  const [numAule, setNumAule] = useState(3);
  const [draggingId, setDraggingId] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [dropHighlight, setDropHighlight] = useState(null);
  const [moveMode, setMoveMode] = useState(null); // {evId, isRec} quando attivo su mobile
  const [moveConfirm, setMoveConfirm] = useState(null); // {id, orig, newDate, newH, endH, endM, occurrenceDate}
  const [editConfirm, setEditConfirm] = useState(null); // {data, occurrenceDate} — modifica ricorrente

  // Refs stabili per usarli nei listener nativi (evitano closure stale)
  const moveEventRef = useRef(null);
  const openEditRef = useRef(null);
  const eventsMapRef = useRef({});

  const H = 48;
  const HOURS = Array.from({length:15},(_,i)=>i+8);
  const today = new Date();
  const isNarrow = window.innerWidth < 700;

  const colorFor = (m) => maestriColors[(m||'').toUpperCase()]||'#7a8fa0';
  const fmtT = (h,m) => `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const getWeekStart = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    date.setDate(date.getDate() - (day===0?6:day-1));
    date.setHours(0,0,0,0);
    return date;
  };

  const getEventsForDate = (date) => {
    if(calHidden) return [];
    const dow = date.getDay();
    const ds = date.toDateString();
    const res = [];
    calRecurring.forEach(e => {
      if(e.dow!==dow) return;
      if(calFiltroMaestro && e.maestro!==calFiltroMaestro) return;
      if(e.startDate && date < new Date(e.startDate+'T00:00:00')) return;
      if(e.endDate && date > new Date(e.endDate+'T23:59:59')) return;
      if(e.recur==='biweekly'){
        if(!e.startDate) return;
        const diffWeeks = Math.round((date - new Date(e.startDate+'T00:00:00')) / (7*24*60*60*1000));
        if(diffWeeks % 2 !== 0) return;
      }
      res.push({...e, _key:`r-${e.id}`, _isRecurring:true});
    });
    calSingle.forEach(e => {
      if(new Date(e.date+'T12:00:00').toDateString()===ds && (!calFiltroMaestro || e.maestro===calFiltroMaestro))
        res.push({...e, _key:`s-${e.id}`, _isRecurring:false});
    });
    return res;
  };

  const navigate = (dir) => {
    const d = new Date(localDate);
    if(localView==='week') d.setDate(d.getDate()+dir*7);
    else if(localView==='month') d.setMonth(d.getMonth()+dir);
    else d.setDate(d.getDate()+dir);
    setLocalDate(d);
  };

  const openNew = (dateStr, hour) => {
    if (isMaestroRole && !readonlyMaestro) {
      notify('Configura il tuo nome maestro nel pannello Utenti prima di aggiungere lezioni.', 'err');
      return;
    }
    const primoAttivo = maestriDB.find(m => !m.archiviato);
    const defaultMaestro = readonlyMaestro
      ? readonlyMaestro
      : (primoAttivo ? `${primoAttivo.nome} ${primoAttivo.cognome}`.trim().toUpperCase() : '');
    setModal({type:'new', dateStr, hour:hour||10, ev:{
      title:'', maestro:defaultMaestro, startH:hour||15, startM:0,
      endH:(hour||15)+1, endM:0, recur:'none', date:dateStr, note:'',
      startDate: dateStr, endDate: ''
    }});
  };

  const canEditEvent = (ev) => !readonlyMaestro || (ev.maestro || '').toUpperCase() === readonlyMaestro.toUpperCase();

  const openEdit = (ev, isRecurring, dateStr) => {
    if (!canEditEvent(ev)) return;
    // recur e date sempre presenti nel form: la ripetizione si può cambiare anche in modifica
    setModal({type:'edit', isRecurring, occurrenceDate: dateStr || null,
      ev:{...ev, recur: ev.recur || (isRecurring ? 'weekly' : 'none'), date: ev.date || dateStr}});
  };

  const fmtISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  // Righe DB → oggetti dello stato. recur va sempre copiato: distingue le serie quindicinali
  const serieDaDB = (s) => ({ id: s.id, title: s.title, maestro: s.maestro,
    startH: s.start_h, startM: s.start_m, endH: s.end_h, endM: s.end_m,
    dow: s.dow, recur: s.recur, note: s.note || '', startDate: s.start_date, endDate: s.end_date });
  const singolaDaDB = (s) => ({ id: s.id, title: s.title, maestro: s.maestro,
    startH: s.start_h, startM: s.start_m, endH: s.end_h, endM: s.end_m, date: s.data, note: s.note || '' });
  // Prima data, da `iso` in poi, che cade nel giorno della settimana `dow`
  const primaData = (iso, dow) => {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + ((dow - d.getDay() + 7) % 7));
    return fmtISO(d);
  };
  const passoSerie = (s) => s.recur === 'biweekly' ? 14 : 7;
  // Chiude la serie il giorno prima di `occIso`. Se la serie non ha lezioni prima di quella
  // data la riga si elimina: chiuderla lascerebbe nel DB una serie vuota (fine prima dell'inizio).
  // Restituisce l'errore del DB, null se è andata bene.
  const chiudiSerie = async (orig, occIso) => {
    const giornoPrima = new Date(occIso + 'T12:00:00'); giornoPrima.setDate(giornoPrima.getDate() - 1);
    const senzaPassato = !!orig.startDate && primaData(orig.startDate, orig.dow) >= occIso;
    const { error } = senzaPassato
      ? await supabase.from('calendario').delete().eq('id', orig.id)
      : await supabase.from('calendario').update({ end_date: fmtISO(giornoPrima) }).eq('id', orig.id);
    if (!error) setCalRecurring(p => senzaPassato
      ? p.filter(e => e.id !== orig.id)
      : p.map(e => e.id === orig.id ? {...e, endDate: fmtISO(giornoPrima)} : e));
    return error;
  };

  const layoutEvents = (events) => {
    if(!events.length) return [];
    const items = events.map(ev => ({
      ev,
      start: ev.startH*60 + ev.startM,
      end:   Math.max(ev.endH*60 + ev.endM, ev.startH*60 + ev.startM + 15),
      col: 0, totalCols: 1,
    })).sort((a,b) => a.start - b.start || b.end - a.end);
    const colEnds = [];
    items.forEach(item => {
      const col = colEnds.findIndex(end => end <= item.start);
      if(col === -1) { item.col = colEnds.length; colEnds.push(item.end); }
      else           { item.col = col;             colEnds[col] = item.end; }
    });
    items.forEach(item => {
      const overlapping = items.filter(o => o.start < item.end && o.end > item.start);
      item.totalCols = Math.max(...overlapping.map(o => o.col)) + 1;
    });
    return items;
  };

  const moveEvent = (id, isRecurring, newDate, newH, isDuplicate, occurrenceDate) => {
    const srcArr = isRecurring ? calRecurring : calSingle;
    const orig = srcArr.find(e => e.id === id);
    if (!orig) return;
    const dur = Math.max((orig.endH - orig.startH) * 60 + (orig.endM - orig.startM), 30);
    const endTotalMin = newH * 60 + dur;
    const endH = Math.min(Math.floor(endTotalMin / 60), 23);
    const endM = endTotalMin % 60;

    setCalView(localView);
    setCalDate(localDate);

    // Per eventi ricorrenti chiedi se spostare solo questa occorrenza o tutte
    if (isRecurring && !isDuplicate) {
      setMoveConfirm({ id, orig, newDate, newH, endH, endM, occurrenceDate: occurrenceDate || newDate });
      return;
    }

    if (isDuplicate) {
      const row = {
        title: orig.title, maestro: orig.maestro,
        start_h: newH, start_m: 0, end_h: endH, end_m: endM,
        recur: 'none', dow: null, data: newDate, note: orig.note || ''
      };
      supabase.from('calendario').insert(row).select().single().then(({ data: saved, error }) => {
        if (error) { notify('Errore duplicazione: ' + error.message, 'err'); return; }
        const newEv = {
          id: saved.id, title: saved.title, maestro: saved.maestro,
          startH: saved.start_h, startM: saved.start_m,
          endH: saved.end_h, endM: saved.end_m,
          date: saved.data, note: saved.note || ''
        };
        setCalSingle(p => [...p, newEv]);
      });
      notify("Lezione duplicata ✓", "ok");
    } else {
      let updated;
      if (isRecurring) {
        const dow = new Date(newDate + 'T12:00:00').getDay();
        updated = {...orig, startH: newH, startM: 0, endH, endM, dow};
        setCalRecurring(p => p.map(e => e.id === id ? updated : e));
      } else {
        updated = {...orig, startH: newH, startM: 0, endH, endM, date: newDate};
        setCalSingle(p => p.map(e => e.id === id ? updated : e));
      }
      saveEventoToDB(updated, isRecurring);
    }
  };

  // Sposta TUTTE le occorrenze da questa in avanti (termina la serie passata, crea una nuova con il nuovo orario)
  const moveAllOccurrences = async () => {
    if (!moveConfirm) return;
    const { id, orig, newDate, newH, endH, endM, occurrenceDate } = moveConfirm;

    // Termina la serie originale il giorno prima dell'occorrenza
    const e1 = await chiudiSerie({...orig, id}, occurrenceDate);
    if (e1) { notify('Errore: ' + e1.message, 'err'); setMoveConfirm(null); return; }

    // Crea nuova serie con il nuovo giorno e orario, dalla data di destinazione in poi
    const { data: saved, error: e2 } = await supabase.from('calendario')
      .insert({ title: orig.title, maestro: orig.maestro,
        start_h: newH, start_m: 0, end_h: endH, end_m: endM,
        recur: orig.recur || 'weekly', dow: new Date(newDate + 'T12:00:00').getDay(), data: null, note: orig.note || '',
        start_date: newDate, end_date: orig.endDate || null })
      .select().single();
    if (e2) { notify('Errore: ' + e2.message, 'err'); setMoveConfirm(null); return; }
    setCalRecurring(p => [...p, serieDaDB(saved)]);
    setMoveConfirm(null);
    notify('Tutte le occorrenze future spostate ✓', 'ok');
  };

  // Sposta SOLO questa occorrenza: spezza la serie e crea un evento singolo
  const moveOnlyOccurrence = async () => {
    if (!moveConfirm) return;
    const { id, orig, newDate, newH, endH, endM, occurrenceDate } = moveConfirm;
    const occ = new Date(occurrenceDate + 'T12:00:00');
    const dayAfter = new Date(occ); dayAfter.setDate(dayAfter.getDate() + passoSerie(orig));
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    // Termina la serie il giorno prima
    const e1 = await chiudiSerie({...orig, id}, occurrenceDate);
    if (e1) { notify('Errore: ' + e1.message, 'err'); setMoveConfirm(null); return; }

    // Crea evento singolo spostato
    const { data: saved, error: e2 } = await supabase.from('calendario')
      .insert({ title: orig.title, maestro: orig.maestro, start_h: newH, start_m: 0,
        end_h: endH, end_m: endM, recur: 'none', dow: null, data: newDate, note: orig.note || '' })
      .select().single();
    if (e2) { notify('Errore: ' + e2.message, 'err'); setMoveConfirm(null); return; }
    setCalSingle(p => [...p, { id: saved.id, title: saved.title, maestro: saved.maestro,
      startH: saved.start_h, startM: saved.start_m, endH: saved.end_h, endM: saved.end_m,
      date: saved.data, note: saved.note || '' }]);

    // Se la serie continua dopo, ricreala
    const originalEndDate = orig.endDate ? new Date(orig.endDate + 'T12:00:00') : null;
    if (!originalEndDate || dayAfter <= originalEndDate) {
      const { data: saved2, error: e3 } = await supabase.from('calendario')
        .insert({ title: orig.title, maestro: orig.maestro, start_h: orig.startH, start_m: orig.startM,
          end_h: orig.endH, end_m: orig.endM, recur: orig.recur || 'weekly', dow: orig.dow, data: null,
          note: orig.note || '', start_date: fmt(dayAfter), end_date: orig.endDate || null })
        .select().single();
      if (!e3) setCalRecurring(p => [...p, serieDaDB(saved2)]);
    }
    setMoveConfirm(null);
    notify('Occorrenza spostata ✓', 'ok');
  };

  // Aggiorna i ref ad ogni render (usati nei listener nativi di window)
  moveEventRef.current = moveEvent;
  openEditRef.current = openEdit;
  eventsMapRef.current = {};
  [...calRecurring, ...calSingle].forEach(e => { eventsMapRef.current[e.id] = e; });


  const saveNew = async (data) => {
    setCalView(localView);
    setCalDate(localDate);
    setModal(null);
    const isRecurring = data.recur === 'weekly' || data.recur === 'biweekly';
    const dow = isRecurring ? new Date(data.date+'T12:00:00').getDay() : null;
    // Se ruolo maestro, forza sempre il proprio nome
    const maestroFinal = readonlyMaestro || data.maestro;
    const row = {
      title: data.title, maestro: maestroFinal,
      start_h: data.startH, start_m: data.startM,
      end_h: data.endH, end_m: data.endM,
      recur: isRecurring ? data.recur : 'none',
      dow: isRecurring ? dow : null,
      data: isRecurring ? null : data.date,
      note: data.note || '',
      start_date: data.startDate || null,
      end_date: data.endDate || null
    };
    const { data: saved, error } = await supabase.from('calendario').insert(row).select().single();
    if (error) { notify('Errore salvataggio lezione: ' + error.message, 'err'); return; }
    const newEv = {
      id: saved.id, title: saved.title, maestro: saved.maestro,
      startH: saved.start_h, startM: saved.start_m,
      endH: saved.end_h, endM: saved.end_m, note: saved.note || '',
      startDate: saved.start_date || null, endDate: saved.end_date || null,
      ...(isRecurring ? {dow: saved.dow, recur: saved.recur} : {date: saved.data})
    };
    if(isRecurring) setCalRecurring(p=>[...p, newEv]);
    else setCalSingle(p=>[...p, newEv]);
  };

  const saveEdit = (data, isRecurring) => {
    setCalView(localView);
    setCalDate(localDate);
    const occurrenceDate = modal?.occurrenceDate || null;
    const diventaSerie = data.recur === 'weekly' || data.recur === 'biweekly';
    if (!isRecurring && diventaSerie) { setModal(null); convertiInSerie(data); return; }
    if (isRecurring && !diventaSerie) { setModal(null); convertiInSingola(data, occurrenceDate); return; }
    if (isRecurring) {
      const orig = calRecurring.find(e => e.id === data.id);
      // Cambio di frequenza (settimanale ↔ quindicinale): vale per forza da qui in poi
      if (orig && (orig.recur || 'weekly') !== data.recur) {
        setModal(null);
        applicaATutteLeFuture(data, occurrenceDate);
        return;
      }
      // Intercetta: chiedi se modificare solo questa occorrenza o tutte le future
      setEditConfirm({ data, occurrenceDate });
      setModal(null);
      return;
    }
    setCalSingle(p=>p.map(e=>e.id===data.id?{...e,...data}:e));
    saveEventoToDB(data, false);
    setModal(null);
  };

  // Lezione singola → serie: la stessa riga diventa ricorrente dal giorno della lezione
  const convertiInSerie = async (data) => {
    const dow = new Date(data.date + 'T12:00:00').getDay();
    const { data: saved, error } = await supabase.from('calendario').update({
      title: data.title, maestro: data.maestro,
      start_h: data.startH, start_m: data.startM, end_h: data.endH, end_m: data.endM,
      recur: data.recur, dow, data: null, note: data.note || '',
      start_date: data.startDate || data.date, end_date: data.endDate || null
    }).eq('id', data.id).select().single();
    if (error) { notify('Errore: ' + error.message, 'err'); return; }
    setCalSingle(p => p.filter(e => e.id !== data.id));
    setCalRecurring(p => [...p, serieDaDB(saved)]);
    notify(data.recur === 'biweekly' ? 'Ora si ripete ogni 2 settimane ✓' : 'Ora si ripete ogni settimana ✓', 'ok');
  };

  // Serie → lezione singola: dalla lezione toccata in poi la serie non si ripete più
  // (le lezioni passate restano) e al suo posto resta una lezione singola nel giorno scelto
  const convertiInSingola = async (data, occurrenceDate) => {
    const orig = calRecurring.find(e => e.id === data.id);
    if (!orig) return;
    const occ = occurrenceDate || fmtISO(new Date());
    const e1 = await chiudiSerie(orig, occ);
    if (e1) { notify('Errore: ' + e1.message, 'err'); return; }
    const { data: saved, error: e2 } = await supabase.from('calendario')
      .insert({ title: data.title, maestro: data.maestro,
        start_h: data.startH, start_m: data.startM, end_h: data.endH, end_m: data.endM,
        recur: 'none', dow: null, data: data.date || occ, note: data.note || '' })
      .select().single();
    if (e2) { notify('Errore: ' + e2.message, 'err'); return; }
    setCalSingle(p => [...p, singolaDaDB(saved)]);
    notify('Ora è una lezione singola ✓', 'ok');
  };

  const editOnlyOccurrence = async () => {
    if (!editConfirm) return;
    const { data, occurrenceDate } = editConfirm;
    const orig = calRecurring.find(e => e.id === data.id);
    if (!orig || !occurrenceDate) { setEditConfirm(null); return; }
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const occ = new Date(occurrenceDate + 'T12:00:00');
    const dayAfter = new Date(occ); dayAfter.setDate(dayAfter.getDate() + passoSerie(orig));

    // Termina la serie il giorno prima
    const e1 = await chiudiSerie(orig, occurrenceDate);
    if (e1) { notify('Errore: ' + e1.message, 'err'); setEditConfirm(null); return; }

    // Crea evento singolo con le modifiche
    const { data: saved, error: e2 } = await supabase.from('calendario')
      .insert({ title: data.title, maestro: data.maestro,
        start_h: data.startH, start_m: data.startM, end_h: data.endH, end_m: data.endM,
        recur: 'none', dow: null, data: occurrenceDate, note: data.note || '' })
      .select().single();
    if (e2) { notify('Errore: ' + e2.message, 'err'); setEditConfirm(null); return; }
    setCalSingle(p => [...p, singolaDaDB(saved)]);

    // Se la serie continua, ricreala invariata (stessa frequenza)
    const originalEndDate = orig.endDate ? new Date(orig.endDate + 'T12:00:00') : null;
    if (!originalEndDate || dayAfter <= originalEndDate) {
      const { data: saved2, error: e3 } = await supabase.from('calendario')
        .insert({ title: orig.title, maestro: orig.maestro,
          start_h: orig.startH, start_m: orig.startM, end_h: orig.endH, end_m: orig.endM,
          recur: orig.recur || 'weekly', dow: orig.dow, data: null, note: orig.note || '',
          start_date: fmt(dayAfter), end_date: orig.endDate || null })
        .select().single();
      if (!e3) setCalRecurring(p => [...p, serieDaDB(saved2)]);
    }
    setEditConfirm(null);
    notify('Occorrenza modificata ✓', 'ok');
  };

  const editAllOccurrences = async () => {
    if (!editConfirm) return;
    const { data, occurrenceDate } = editConfirm;
    setEditConfirm(null);
    applicaATutteLeFuture(data, occurrenceDate);
  };

  // Chiude la serie prima dell'occorrenza e ne crea una nuova con le modifiche (giorno,
  // orario, frequenza compresi) dalla prima data utile in poi
  const applicaATutteLeFuture = async (data, occurrenceDate) => {
    const orig = calRecurring.find(e => e.id === data.id);
    if (!orig) return;
    const occIso = occurrenceDate || fmtISO(new Date());
    const dow = data.dow != null ? Number(data.dow) : orig.dow;

    // Termina la serie originale il giorno prima
    const e1 = await chiudiSerie(orig, occIso);
    if (e1) { notify('Errore: ' + e1.message, 'err'); return; }

    // Crea nuova serie con le modifiche da questa data in poi
    const { data: saved, error: e2 } = await supabase.from('calendario')
      .insert({ title: data.title, maestro: data.maestro,
        start_h: data.startH, start_m: data.startM, end_h: data.endH, end_m: data.endM,
        recur: data.recur || orig.recur || 'weekly', dow, data: null, note: data.note || '',
        start_date: primaData(occIso, dow), end_date: data.endDate || null })
      .select().single();
    if (e2) { notify('Errore: ' + e2.message, 'err'); return; }
    setCalRecurring(p => [...p, serieDaDB(saved)]);
    notify('Occorrenze future modificate ✓', 'ok');
  };

  const deleteEv = async (id, isRecurring) => {
    const { error } = await supabase.from('calendario').delete().eq('id', id);
    if (error) { notify('Errore eliminazione: ' + error.message, 'err'); return; }
    setCalView(localView); setCalDate(localDate);
    if(isRecurring) setCalRecurring(p=>p.filter(e=>e.id!==id));
    else setCalSingle(p=>p.filter(e=>e.id!==id));
    setModal(null);
  };

  // Elimina questa occorrenza e tutte le future: tronca la serie al giorno prima
  const deleteFutureOccurrences = async (id, occurrenceDate) => {
    const ev = calRecurring.find(e => e.id === id);
    if (!ev) return;
    // Dalla prima lezione della serie: la riga si elimina; altrimenti si chiude al giorno prima
    const error = await chiudiSerie(ev, occurrenceDate);
    if (error) { notify('Errore: ' + error.message, 'err'); return; }
    setCalView(localView); setCalDate(localDate);
    setModal(null);
    notify('Occorrenze future eliminate ✓', 'ok');
  };

  // Elimina solo l'occorrenza della data specificata: spezza la serie in due
  const deleteOnlyOccurrence = async (ev, occurrenceDate) => {
    // Frequenza dalla serie salvata, non dal form (che potrebbe essere stato cambiato)
    const serie = calRecurring.find(e => e.id === ev.id) || ev;
    const occ = new Date(occurrenceDate + 'T12:00:00');
    const dayAfter  = new Date(occ); dayAfter.setDate(dayAfter.getDate() + passoSerie(serie)); // prossima lezione della serie
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    // Aggiorna evento originale: termina il giorno prima dell'occorrenza
    const e1 = await chiudiSerie(serie, occurrenceDate);
    if (e1) { notify('Errore: ' + e1.message, 'err'); return; }

    // Se esistono occorrenze future, crea una nuova serie dal giorno dopo
    const originalEndDate = ev.endDate ? new Date(ev.endDate + 'T12:00:00') : null;
    if (!originalEndDate || dayAfter <= originalEndDate) {
      const row = {
        title: ev.title, maestro: ev.maestro,
        start_h: ev.startH, start_m: ev.startM,
        end_h: ev.endH, end_m: ev.endM,
        recur: serie.recur || 'weekly', dow: ev.dow, data: null, note: ev.note || '',
        start_date: fmt(dayAfter),
        end_date: ev.endDate || null,
      };
      const { data: saved, error: e2 } = await supabase.from('calendario').insert(row).select().single();
      if (e2) { notify('Errore creazione serie futura: ' + e2.message, 'err'); return; }
      setCalRecurring(p => [...p, serieDaDB(saved)]);
    }
    setModal(null);
    notify('Occorrenza eliminata.');
  };

  // ── EVENT MODAL ──────────────────────────────────────────────────────────────
  const EventModal = () => {
    if(!modal) return null;
    const isEdit = modal.type==='edit';
    const [form, setForm] = useState(modal.ev);
    const [showDeleteChoice, setShowDeleteChoice] = useState(false);
    const f = k => e => {
      const v = e.target.value;
      if(k==='startTime'){
        const [h,m]=v.split(':').map(Number);
        setForm(p=>({...p,startH:h,startM:m,endH:h+1,endM:m}));
      } else if(k==='endTime'){
        const [h,m]=v.split(':').map(Number);
        setForm(p=>({...p,endH:h,endM:m}));
      } else setForm(p=>({...p,[k]:v}));
    };
    const serieForm = form.recur==='weekly' || form.recur==='biweekly';
    const dateVal = form.date||`${localDate.getFullYear()}-${String(localDate.getMonth()+1).padStart(2,'0')}-${String(localDate.getDate()).padStart(2,'0')}`;

    return (
      <>
      {showDeleteChoice && modal.isRecurring && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",zIndex:300,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:P.card,border:`1px solid ${P.red}55`,borderRadius:16,
            padding:28,width:320,maxWidth:"88vw"}}>
            <div style={{fontSize:16,fontWeight:600,color:P.text,marginBottom:6}}>
              Elimina evento ricorrente
            </div>
            <div style={{fontSize:14,color:P.secondary,marginBottom:18}}>
              Cosa vuoi eliminare?
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button onClick={()=>deleteOnlyOccurrence(form, modal.occurrenceDate)}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.red}55`,
                  background:`${P.red}18`,color:P.red,cursor:"pointer",fontSize:15,
                  textAlign:"left",fontWeight:500}}>
                Solo questa occorrenza
              </button>
              <button onClick={()=>deleteFutureOccurrences(form.id, modal.occurrenceDate)}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.red}55`,
                  background:`${P.red}18`,color:P.red,cursor:"pointer",fontSize:15,
                  textAlign:"left",fontWeight:500}}>
                Questa e tutte le future
              </button>
              <button onClick={()=>setShowDeleteChoice(false)}
                style={{padding:"10px 14px",borderRadius:9,border:`1px solid ${P.border}`,
                  background:"transparent",color:P.muted,cursor:"pointer",fontSize:15,marginTop:4}}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:200,
        display:"flex",alignItems:"center",justifyContent:"center"}}
        onClick={e=>{ if(e.target===e.currentTarget) setModal(null); }}>
        <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:16,
          padding:28,width:380,maxWidth:"94vw",maxHeight:"90vh",overflowY:"auto"}}
          onKeyDown={e=>{ if(e.key==="Enter") { e.preventDefault(); isEdit?saveEdit(form,modal.isRecurring):saveNew(form); } }}>
          <div style={{fontSize:17,fontWeight:500,marginBottom:16,color:P.text}}>
            {isEdit?"Modifica lezione":"Nuova lezione"}
          </div>

          {[
            {label:"Titolo / Allievo",key:"title",type:"text",ph:"Es. Mario Rossi"},
            {label:"Note",key:"note",type:"text",ph:"Note opzionali"},
          ].map(({label,key,type,ph})=>(
            <div key={key} style={{marginBottom:12}}>
              <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>{label.toUpperCase()}</label>
              <input type={type} value={form[key]||''} onChange={f(key)} placeholder={ph}
                style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                  borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15}}/>
            </div>
          ))}

          <div style={{marginBottom:12}}>
            <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>MAESTRO</label>
            {readonlyMaestro ? (
              <div style={{padding:"8px 10px",background:P.bg,border:`1px solid ${P.border}`,
                borderRadius:8,fontSize:15,color:P.text}}>{readonlyMaestro}</div>
            ) : (
              <select value={form.maestro} onChange={f('maestro')}
                style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                  borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15,cursor:"pointer"}}>
                {/* archiviati esclusi, tranne il maestro della lezione che si sta modificando */}
                {maestriDB.map(m=>({k:`${m.nome} ${m.cognome}`.trim().toUpperCase(),arch:m.archiviato}))
                  .filter(m=>!m.arch||m.k===form.maestro).map(m=>m.k)
                  .sort((a,b)=>a.localeCompare(b)).map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>

          {/* Serie esistente che resta serie: si sceglie il giorno; altrimenti la data */}
          {isEdit && modal.isRecurring && serieForm ? (
            <div style={{marginBottom:12}}>
              <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>GIORNO</label>
              <select value={form.dow} onChange={e=>setForm(p=>({...p,dow:parseInt(e.target.value)}))}
                style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                  borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15,cursor:"pointer"}}>
                {DAYS_IT_CAL.map((d,i)=><option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          ) : (
            <div style={{marginBottom:12}}>
              <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>
                {serieForm ? "DATA PRIMA LEZIONE" : "DATA"}
              </label>
              <input type="date" value={form.date||dateVal}
                onChange={e=>setForm(p=>({...p,date:e.target.value}))}
                style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                  borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15}}/>
            </div>
          )}

          <div style={{marginBottom:12}}>
            <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>RIPETIZIONE</label>
            <select value={form.recur||'none'}
              onChange={e=>{ const v=e.target.value;
                setForm(p=>({...p,recur:v,startDate:(v!=='none'&&!p.startDate)?(p.date||dateVal):p.startDate})); }}
              style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15,cursor:"pointer"}}>
              <option value="none">Nessuna (evento singolo)</option>
              <option value="weekly">Ogni settimana</option>
              <option value="biweekly">Ogni 2 settimane</option>
            </select>
            {isEdit && modal.isRecurring && !serieForm && (
              <div style={{fontSize:13,color:P.gold,marginTop:6,lineHeight:1.5}}>
                Da questa lezione in poi non si ripeterà più: resta solo la lezione nella data scelta.
                Le lezioni già passate non cambiano.
              </div>
            )}
            {isEdit && !modal.isRecurring && serieForm && (
              <div style={{fontSize:13,color:P.gold,marginTop:6,lineHeight:1.5}}>
                Diventerà una lezione che si ripete, a partire dalla data della prima lezione.
              </div>
            )}
            {isEdit && modal.isRecurring && serieForm && (modal.ev.recur||'weekly')!==form.recur && (
              <div style={{fontSize:13,color:P.gold,marginTop:6,lineHeight:1.5}}>
                La nuova frequenza vale da questa lezione in poi.
              </div>
            )}
          </div>

          {serieForm && (
            <div style={{display:"grid",gridTemplateColumns:(isEdit&&modal.isRecurring)?"1fr":"1fr 1fr",gap:10,marginBottom:12}}>
              {/* Per una serie esistente l'inizio lo decide la lezione toccata: solo la fine è modificabile */}
              {!(isEdit && modal.isRecurring) && (
                <div>
                  <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>DAL</label>
                  <input type="date" value={form.startDate||''}
                    onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}
                    style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                      borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15}}/>
                </div>
              )}
              <div>
                <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>AL (opzionale)</label>
                <input type="date" value={form.endDate||''}
                  onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}
                  style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,
                    borderRadius:8,padding:"8px 10px",color:P.text,fontSize:15}}/>
              </div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            {[['INIZIO','startH','startM',true],['FINE','endH','endM',false]].map(([label,hKey,mKey,isStart])=>(
              <div key={label}>
                <label style={{fontSize:13,color:P.muted,display:"block",marginBottom:4}}>{label}</label>
                <div style={{display:"flex",gap:4}}>
                  <select value={form[hKey]}
                    onChange={e=>{const h=+e.target.value;setForm(p=>({...p,[hKey]:h,...(isStart?{endH:Math.min(h+1,23)}:{})}));}}
                    style={{flex:1,background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                      padding:"8px 4px",color:P.text,fontSize:15,cursor:"pointer"}}>
                    {Array.from({length:16},(_,i)=>i+8).map(h=>(
                      <option key={h} value={h}>{String(h).padStart(2,'0')}</option>
                    ))}
                  </select>
                  <select value={form[mKey]}
                    onChange={e=>setForm(p=>({...p,[mKey]:+e.target.value}))}
                    style={{flex:1,background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                      padding:"8px 4px",color:P.text,fontSize:15,cursor:"pointer"}}>
                    {[0,5,10,15,20,25,30,35,40,45,50,55].map(m=>(
                      <option key={m} value={m}>{String(m).padStart(2,'0')}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {isEdit && canEditEvent(modal.ev) && (
            <button onClick={()=>{ setMoveMode({evId:form.id, isRec:modal.isRecurring, occurrenceDate:modal.occurrenceDate}); setModal(null); }}
              style={{width:"100%",padding:"10px",borderRadius:8,border:`1px solid ${P.blue}55`,
                background:`${P.blue}18`,color:P.blue,cursor:"pointer",fontSize:15,
                fontWeight:600,marginTop:18,marginBottom:8}}>
              ↕ Sposta in un altro slot
            </button>
          )}
          <div style={{display:"flex",gap:8,marginTop: isEdit && canEditEvent(modal.ev) ? 0 : 18}}>
            {isEdit && canEditEvent(modal.ev) && (
              <button onClick={()=> modal.isRecurring ? setShowDeleteChoice(true) : deleteEv(form.id, false)}
                style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${P.red}44`,
                  background:`${P.red}15`,color:P.red,cursor:"pointer",fontSize:15}}>
                Elimina
              </button>
            )}
            <button onClick={()=>setModal(null)}
              style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${P.border}`,
                background:"transparent",color:P.muted,cursor:"pointer",fontSize:15}}>
              Annulla
            </button>
            <button onClick={()=>isEdit?saveEdit(form,modal.isRecurring):saveNew(form)}
              style={{flex:1,padding:"8px",borderRadius:8,border:"none",
                background:P.gold,color:"#fff",cursor:"pointer",fontWeight:600,fontSize:15}}>
              Salva
            </button>
          </div>
        </div>
      </div>
      </>
    );
  };

  // ── WEEK VIEW ────────────────────────────────────────────────────────────────
  const WeekView = () => {
    const ws = getWeekStart(localDate);
    const days = Array.from({length:6},(_,i)=>{ const d=new Date(ws); d.setDate(ws.getDate()+i); return d; });
    const scrollRef = useRef(null);
    useEffect(()=>{
      if(scrollRef.current) scrollRef.current.scrollTop = (14-8)*H;
    },[]);
    return (
      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"hidden"}}>
        <div style={{display:"flex",borderBottom:`1px solid ${P.border}`,background:P.card,position:"sticky",top:0,zIndex:5}}>
          <div style={{width:isNarrow?38:52,flexShrink:0}}/>
          {days.map(d=>{
            const isToday=d.toDateString()===today.toDateString();
            const dayLabel=isNarrow?["L","Ma","Me","G","V","S"][d.getDay()-1]:DAYS_IT_CAL[d.getDay()];
            return (
              <div key={d.toString()} style={{flex:1,minWidth:0,textAlign:"center",padding:isNarrow?"5px 2px":"8px 4px",borderLeft:`1px solid ${P.border}`}}>
                <div style={{fontSize:isNarrow?11:12,letterSpacing:isNarrow?0:1,color:isToday?P.gold:P.muted,textTransform:"uppercase"}}>{dayLabel}</div>
                <div style={{width:isNarrow?22:30,height:isNarrow?22:30,borderRadius:"50%",margin:isNarrow?"2px auto 0":"3px auto 0",
                  background:isToday?P.gold:"transparent",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:isNarrow?14:16,fontWeight:isToday?700:400,
                  color:isToday?"#0a0c14":P.text}}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        <div ref={scrollRef} data-calscroll="1" style={{overflowY:"auto",overflowX:"hidden",maxHeight:520}}>
          <div style={{display:"flex",width:"100%"}}>
            <div style={{width:isNarrow?38:52,flexShrink:0}}>
              {HOURS.map(h=>(
                <div key={h} style={{height:H,display:"flex",alignItems:"flex-start",
                  justifyContent:"flex-end",paddingRight:isNarrow?3:6,paddingTop:2,
                  fontSize:isNarrow?13:15,color:P.secondary,borderBottom:`1px solid ${P.border}`}}>
                  {h}:00
                </div>
              ))}
            </div>
            {days.map(d=>{
              const events = getEventsForDate(d);
              const isToday = d.toDateString()===today.toDateString();
              const nowTop = isToday ? ((today.getHours()-8)*H+(today.getMinutes()/60)*H) : -1;
              return (
                <div key={d.toString()} style={{flex:1,minWidth:0,position:"relative",overflow:"hidden",
                  borderLeft:`1px solid ${P.border}`,
                  background:isToday?`${P.gold}04`:"transparent"}}>
                  {HOURS.map(h=>{
                    const slotKey = `${fmtDate(d)}-${h}`;
                    const isHighlit = dropHighlight === slotKey;
                    return (
                      <div key={h}
                        data-day={fmtDate(d)}
                        data-hour={String(h)}
                        style={{height:H,borderBottom:`1px solid ${P.border}`,
                          cursor:"pointer",position:"relative",
                          background: isHighlit ? (isDuplicating?`${P.green}18`:`${P.gold}14`) : moveMode ? `${P.blue}09` : "transparent",
                          outline: isHighlit ? `2px dashed ${isDuplicating?P.green:P.gold}` : moveMode ? `1px dashed ${P.blue}44` : "none",
                          outlineOffset:"-2px", transition:"background .08s"}}
                        onClick={()=>{ if(moveMode){ moveEvent(moveMode.evId,moveMode.isRec,fmtDate(d),h,false,moveMode.occurrenceDate); setMoveMode(null); } else openNew(fmtDate(d),h); }}
                        onDragOver={e=>{
                          e.preventDefault(); e.stopPropagation();
                          const dup = e.altKey;
                          e.dataTransfer.dropEffect = dup ? 'copy' : 'move';
                          setIsDuplicating(dup); setDropHighlight(slotKey);
                        }}
                        onDragEnter={e=>{e.preventDefault();setDropHighlight(slotKey);}}
                        onDragLeave={()=>setDropHighlight(null)}
                        onDrop={e=>{
                          e.preventDefault(); e.stopPropagation();
                          const targetDay = e.currentTarget.dataset.day;
                          const targetHour = parseInt(e.currentTarget.dataset.hour);
                          const evId = parseInt(e.dataTransfer.getData('evId'));
                          const isRec = e.dataTransfer.getData('isRec') === 'true';
                          const dup = e.altKey;
                          setDropHighlight(null); setDraggingId(null); setIsDuplicating(false);
                          if (evId && targetDay) moveEvent(evId, isRec, targetDay, targetHour, dup);
                        }}>
                        <div style={{position:"absolute",bottom:0,left:0,right:0,
                          height:"50%",borderTop:`0.5px dashed ${P.border}`,opacity:.5,pointerEvents:"none"}}/>
                      </div>
                    );
                  })}
                  {nowTop>=0 && (
                    <div style={{position:"absolute",left:0,right:0,top:nowTop,
                      height:2,background:P.red,zIndex:3,pointerEvents:"none"}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:P.red,
                        position:"absolute",left:-4,top:-3}}/>
                    </div>
                  )}
                  {(()=>{
                    const SLIVER=5; // px per striscia nascosta
                    const laid=layoutEvents(events);
                    return laid.map(({ev,col,totalCols})=>{
                      const top=(ev.startH-8)*H+(ev.startM/60)*H;
                      const dur=(ev.endH-ev.startH)*60+(ev.endM-ev.startM);
                      const height=Math.max((dur/60)*H,22);
                      const c=colorFor(ev.maestro);
                      const rgb=`${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)}`;
                      const numHidden=totalCols-1; // quante strisce a sinistra
                      const isBeingDragged=draggingId===ev.id;
                      const dateStr=fmtDate(d||localDate);

                      if(col===0){
                        // Evento principale — shift a destra per far spazio alle strisce
                        const leftOff=numHidden*SLIVER+2;
                        return (
                          <div key={ev._key}
                            onClick={evt=>{ if(evt.pointerType==='touch'||window.matchMedia('(pointer:coarse)').matches) openEdit(ev,ev._isRecurring,dateStr); }}
                            onMouseDown={evt=>{
                              if(evt.button!==0) return; evt.preventDefault();
                              const evSnap={id:ev.id,isRec:ev._isRecurring,dateStr};
                              let moved=false,tDay=null,tHour=null;
                              const getSlot=(x,y)=>document.elementsFromPoint(x,y).find(n=>n.dataset?.day&&n.dataset?.hour!==undefined);
                              const onMove=(e)=>{ if(!moved&&Math.abs(e.clientX-evt.clientX)<12&&Math.abs(e.clientY-evt.clientY)<12)return; moved=true; setDraggingId(evSnap.id); const slot=getSlot(e.clientX,e.clientY); if(slot){tDay=slot.dataset.day;tHour=+slot.dataset.hour;setDropHighlight(`${tDay}-${tHour}`);}else{tDay=null;setDropHighlight(null);}};
                              const onUp=()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); setDropHighlight(null);setDraggingId(null); if(moved){if(tDay!=null)moveEventRef.current(evSnap.id,evSnap.isRec,tDay,tHour,false,evSnap.dateStr);}else{openEditRef.current(ev,ev._isRecurring,evSnap.dateStr);}};
                              window.addEventListener('mousemove',onMove); window.addEventListener('mouseup',onUp);
                            }}
                            data-calevid={ev.id} data-calevrec={String(ev._isRecurring)} data-calevday={dateStr}
                            style={{position:"absolute",left:leftOff,right:2,top,height,
                              background:`rgba(${rgb},0.18)`,borderLeft:`3px solid ${c}`,borderRadius:4,
                              padding:isNarrow?"1px 3px":"2px 5px",cursor:isBeingDragged?"grabbing":"grab",
                              overflow:"hidden",zIndex:3,touchAction:"none",userSelect:"none",
                              WebkitUserSelect:"none",WebkitTouchCallout:"none",
                              opacity:isBeingDragged?0.35:1,transition:"opacity .1s"}}>
                            {isNarrow ? (height>28 ? <>
                              <div style={{fontSize:11,fontWeight:600,color:c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",pointerEvents:"none"}}>{ev.title}</div>
                              <div style={{fontSize:11,color:c,opacity:.75,pointerEvents:"none"}}>{ev.maestro.split(' ')[0]}</div>
                            </> : <div style={{fontSize:11,fontWeight:600,color:c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",pointerEvents:"none"}}>{ev.title}</div>
                            ) : <>
                              <div style={{fontSize:15,fontWeight:600,color:c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",pointerEvents:"none"}}>{ev.title}</div>
                              <div style={{fontSize:11,color:c,opacity:.85,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.maestro}</div>
                            </>}
                          </div>
                        );
                      } else {
                        // Evento fantasma: blocco intero trasparente + bordo colorato che sbuca a sinistra
                        const ghostLeft=(col-1)*SLIVER;
                        return (
                          <div key={ev._key}
                            onClick={()=>openEdit(ev,ev._isRecurring,dateStr)}
                            style={{position:"absolute",left:ghostLeft,right:2,top,height,
                              background:`rgba(${rgb},0.07)`,borderLeft:`3px solid ${c}`,borderRadius:4,
                              zIndex:numHidden+1-col,cursor:"pointer",touchAction:"none"}}/>
                        );
                      }
                    });
                  })()}
                </div>
              );
            })}
          </div>
        </div>
        {/* Riga "altri X eventi" sotto la griglia */}
        <div style={{display:"flex",borderTop:`1px solid ${P.border}`}}>
          <div style={{width:isNarrow?38:52,flexShrink:0}}/>
          {days.map(d=>{
            const allLaid=layoutEvents(getEventsForDate(d));
            const hidden=allLaid.filter(x=>x.col>0).length;
            return (
              <div key={d.toString()} style={{flex:1,borderLeft:`1px solid ${P.border}`,
                padding:isNarrow?"3px 2px":"5px 3px",textAlign:"center",minHeight:isNarrow?22:28}}>
                {hidden>0 && (
                  <div onClick={()=>{setLocalDate(d);setLocalView('day');}}
                    style={{fontSize:isNarrow?11:12,color:P.gold,cursor:"pointer",fontWeight:600,
                      lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    +{hidden}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── DAY VIEW ─────────────────────────────────────────────────────────────────
  const DayView = () => {
    const events = getEventsForDate(localDate);
    const isToday = localDate.toDateString()===today.toDateString();
    const nowTop = isToday ? ((today.getHours()-8)*H+(today.getMinutes()/60)*H) : -1;
    const scrollRef = useRef(null);
    useEffect(()=>{
      if(scrollRef.current) scrollRef.current.scrollTop = (14-8)*H; // scrolla alle 14:00
    },[]);
    return (
      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"hidden"}}>
        <div ref={scrollRef} data-calscroll="1" style={{overflowY:"auto",overflowX:"hidden",maxHeight:580}}>
          <div style={{display:"flex",width:"100%"}}>
            <div style={{width:52,flexShrink:0}}>
              {HOURS.map(h=>(
                <div key={h} style={{height:H,display:"flex",alignItems:"flex-start",
                  justifyContent:"flex-end",paddingRight:6,paddingTop:2,
                  fontSize:15,color:P.secondary,borderBottom:`1px solid ${P.border}`}}>{h}:00</div>
              ))}
            </div>
            <div style={{flex:1,minWidth:0,position:"relative",overflow:"hidden",borderLeft:`1px solid ${P.border}`}}>
              {HOURS.map(h=>{
                const dayStr = fmtDate(localDate);
                const slotKey = `${dayStr}-${h}`;
                const isHighlit = dropHighlight === slotKey;
                return (
                  <div key={h}
                    data-day={dayStr}
                    data-hour={String(h)}
                    style={{height:H,borderBottom:`1px solid ${P.border}`,cursor:"pointer",
                      background: isHighlit ? (isDuplicating?`${P.green}18`:`${P.gold}14`) : moveMode ? `${P.blue}09` : "transparent",
                      outline: isHighlit ? `2px dashed ${isDuplicating?P.green:P.gold}` : moveMode ? `1px dashed ${P.blue}44` : "none",
                      outlineOffset:"-2px", transition:"background .08s"}}
                    onClick={()=>{ if(moveMode){ moveEvent(moveMode.evId,moveMode.isRec,dayStr,h,false,moveMode.occurrenceDate); setMoveMode(null); } else openNew(dayStr,h); }}
                    onDragOver={e=>{
                      e.preventDefault(); e.stopPropagation();
                      const dup = e.altKey;
                      e.dataTransfer.dropEffect = dup ? 'copy' : 'move';
                      setIsDuplicating(dup); setDropHighlight(slotKey);
                    }}
                    onDragEnter={e=>{e.preventDefault();setDropHighlight(slotKey);}}
                    onDragLeave={()=>setDropHighlight(null)}
                    onDrop={e=>{
                      e.preventDefault(); e.stopPropagation();
                      const targetDay = e.currentTarget.dataset.day;
                      const targetHour = parseInt(e.currentTarget.dataset.hour);
                      const evId = parseInt(e.dataTransfer.getData('evId'));
                      const isRec = e.dataTransfer.getData('isRec') === 'true';
                      const dup = e.altKey;
                      setDropHighlight(null); setDraggingId(null); setIsDuplicating(false);
                      if (evId && targetDay) moveEvent(evId, isRec, targetDay, targetHour, dup);
                    }}>
                    <div style={{height:"50%",borderTop:`0.5px dashed ${P.border}`,opacity:.5,pointerEvents:"none"}}/>
                  </div>
                );
              })}
              {nowTop>=0 && (
                <div style={{position:"absolute",left:0,right:0,top:nowTop,height:2,
                  background:P.red,zIndex:3,pointerEvents:"none"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:P.red,
                    position:"absolute",left:-4,top:-3}}/>
                </div>
              )}
              {layoutEvents(events).map(({ev,col,totalCols})=>{
                const top=(ev.startH-8)*H+(ev.startM/60)*H;
                const dur=(ev.endH-ev.startH)*60+(ev.endM-ev.startM);
                const height=Math.max((dur/60)*H,22);
                const c=colorFor(ev.maestro);
                const gap=2;
                const leftPct=(col/totalCols)*100;
                const widthPct=(1/totalCols)*100;
                const isBeingDragged = draggingId === ev.id;
                const dayDateStr = fmtDate(localDate);
                return (
                  <div key={ev._key}
                    onClick={evt=>{ if(evt.pointerType==='touch'||window.matchMedia('(pointer:coarse)').matches) openEdit(ev,ev._isRecurring,dayDateStr); }}
                    onMouseDown={evt=>{
                      if(evt.button!==0) return;
                      evt.preventDefault();
                      const el=evt.currentTarget, sx=evt.clientX, sy=evt.clientY;
                      const evSnap={id:ev.id,isRec:ev._isRecurring,dateStr:dayDateStr};
                      let moved=false, tDay=null, tHour=null;
                      const getSlot=(x,y)=>document.elementsFromPoint(x,y).find(n=>n.dataset?.day&&n.dataset?.hour!==undefined);
                      const onMove=(e)=>{
                        if(!moved&&Math.abs(e.clientX-sx)<12&&Math.abs(e.clientY-sy)<12)return;
                        moved=true; setDraggingId(evSnap.id);
                        const slot=getSlot(e.clientX,e.clientY);
                        if(slot){tDay=slot.dataset.day;tHour=+slot.dataset.hour;setDropHighlight(`${tDay}-${tHour}`);}
                        else{tDay=null;setDropHighlight(null);}
                      };
                      const onUp=(e)=>{
                        window.removeEventListener('mousemove',onMove);
                        window.removeEventListener('mouseup',onUp);
                        setDropHighlight(null);setDraggingId(null);
                        if(moved){if(tDay!=null)moveEventRef.current(evSnap.id,evSnap.isRec,tDay,tHour,false);}
                        else{openEditRef.current(ev,ev._isRecurring,evSnap.dateStr);}
                      };
                      window.addEventListener('mousemove',onMove);
                      window.addEventListener('mouseup',onUp);
                    }}
                    data-calevid={ev.id}
                    data-calevrec={String(ev._isRecurring)}
                    data-calevday={dayDateStr}
                    style={{position:"absolute",
                      left:`calc(${leftPct}% + ${gap}px)`,
                      width:`calc(${widthPct}% - ${gap*2}px)`,
                      top,height,
                      background:`rgba(${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)},0.18)`,
                      borderLeft:`3px solid ${c}`,borderRadius:4,
                      padding: totalCols >= 3 ? "2px 3px" : "3px 6px",
                      cursor:isBeingDragged?"grabbing":"grab",overflow:"hidden",zIndex:2,
                      touchAction:"none",userSelect:"none",WebkitUserSelect:"none",WebkitTouchCallout:"none",
                      opacity: isBeingDragged ? 0.35 : 1,
                      transition:"opacity .1s"}}>
                    {/* Testo adattivo in base al numero di eventi sovrapposti */}
                    {totalCols <= 3 && <>
                      <div style={{fontSize:16,fontWeight:600,color:c,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.title}</div>
                      <div style={{fontSize:11,color:c,opacity:.85,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.maestro}</div>
                    </>}
                    {totalCols === 4 && <>
                      <div style={{fontSize:13,fontWeight:600,color:c,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.title.split(' ')[0]}</div>
                      <div style={{fontSize:11,color:c,opacity:.85,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.maestro.split(' ')[0]}</div>
                    </>}
                    {totalCols >= 5 && <>
                      <div style={{fontSize:11,fontWeight:600,color:c,pointerEvents:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ev.title.split(' ').map(w=>w[0]).join('')}</div>
                    </>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── MONTH VIEW ───────────────────────────────────────────────────────────────
  const MonthView = () => {
    const year=localDate.getFullYear(), month=localDate.getMonth();
    const firstDay=new Date(year,month,1);
    const lastDay=new Date(year,month+1,0);
    // Genera settimane Lun-Sab, salta la domenica
    const weeks=[];
    const cur=getWeekStart(firstDay);
    while(cur<=lastDay){
      const week=[];
      for(let i=0;i<6;i++){week.push(new Date(cur));cur.setDate(cur.getDate()+1);}
      cur.setDate(cur.getDate()+1); // salta domenica
      weeks.push(week);
    }
    return (
      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",borderBottom:`1px solid ${P.border}`}}>
          {(isNarrow ? ["L","Ma","Me","G","V","S"] : ["Lun","Mar","Mer","Gio","Ven","Sab"]).map((d,i)=>(
            <div key={i} style={{padding:isNarrow?"5px 2px":"8px 4px",textAlign:"center",
              fontSize:isNarrow?11:12,letterSpacing:isNarrow?0:1,
              color:P.muted,textTransform:"uppercase"}}>{d}</div>
          ))}
        </div>
        {weeks.map((week,wi)=>(
          <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)"}}>
            {week.map((date,di)=>{
              const inMonth=date.getMonth()===month;
              const isToday=date.toDateString()===today.toDateString();
              const events=inMonth?getEventsForDate(date):[];
              return (
                <div key={di} style={{
                  minHeight:isNarrow?56:80,minWidth:0,overflow:"hidden",
                  borderRight:`1px solid ${P.border}`,
                  borderBottom:wi<weeks.length-1?`1px solid ${P.border}`:'none',
                  padding:isNarrow?2:4,cursor:inMonth?"pointer":"default",
                  background:isToday?`${P.gold}08`:inMonth?"transparent":`${P.border}22`}}
                  onClick={()=>{if(inMonth){setLocalDate(date);setLocalView('day');}}}>
                  {inMonth && <>
                    <div style={{
                      width:isNarrow?18:24,height:isNarrow?18:24,borderRadius:"50%",
                      background:isToday?P.gold:"transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:isNarrow?12:14,fontWeight:isToday?700:400,
                      color:isToday?"#0a0c14":P.text,marginBottom:isNarrow?1:3}}>
                      {date.getDate()}
                    </div>
                    {events.slice(0,2).map(ev=>{
                      const c=colorFor(ev.maestro);
                      return (
                        <div key={ev._key} onClick={e=>{e.stopPropagation();openEdit(ev,ev._isRecurring,fmtDate(date));}}
                          style={{
                            fontSize:isNarrow?11:12,lineHeight:1.3,
                            padding:isNarrow?"1px 2px":"1px 4px",
                            borderRadius:3,marginBottom:1,
                            background:`rgba(${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)},0.2)`,
                            color:c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",cursor:"pointer"}}>
                          {isNarrow
                            ? fmtT(ev.startH,ev.startM)
                            : `${fmtT(ev.startH,ev.startM)} ${ev.title}`}
                        </div>
                      );
                    })}
                    {events.length>2 && (
                      <div style={{fontSize:isNarrow?11:12,color:P.muted}}>+{events.length-2}</div>
                    )}
                  </>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // ── AULE VIEW ────────────────────────────────────────────────────────────────
  const AuleView = () => {
    const AULE_TOTALI = numAule;
    const ws = getWeekStart(localDate);
    const days = Array.from({length:6},(_,i)=>{ const d=new Date(ws); d.setDate(ws.getDate()+i); return d; });

    const auleColor = (libere) => {
      if(libere >= numAule) return '#16a34a';
      if(libere >= Math.ceil(numAule * 0.66)) return '#d97706';
      if(libere >= 1) return '#c2410c';
      return '#b91c1c';
    };

    const countInSlot = (events, h) => {
      const slotStart = h * 60, slotEnd = (h + 1) * 60;
      const relevant = events.filter(ev => {
        const s = ev.startH*60 + ev.startM;
        const e = ev.endH*60 + ev.endM;
        return s < slotEnd && e > slotStart;
      });
      const points = [];
      relevant.forEach(ev => {
        const s = Math.max(ev.startH*60 + ev.startM, slotStart);
        const e = Math.min(ev.endH*60 + ev.endM, slotEnd);
        points.push([s, 1], [e, -1]);
      });
      points.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
      let cur = 0, peak = 0;
      for (const [, d] of points) { cur += d; if (cur > peak) peak = cur; }
      return peak;
    };

    return (
      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"hidden"}}>
        <div style={{display:"flex",borderBottom:`1px solid ${P.border}`,background:P.card,position:"sticky",top:0,zIndex:5}}>
          <div style={{width:52,flexShrink:0}}/>
          {days.map(d=>{
            const isToday=d.toDateString()===today.toDateString();
            return (
              <div key={d.toString()} style={{flex:1,textAlign:"center",padding:"8px 4px",borderLeft:`1px solid ${P.border}`}}>
                <div style={{fontSize:12,letterSpacing:1,color:isToday?P.gold:P.muted,textTransform:"uppercase"}}>{DAYS_IT_CAL[d.getDay()]}</div>
                <div style={{width:30,height:30,borderRadius:"50%",margin:"3px auto 0",
                  background:isToday?P.gold:"transparent",display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:16,fontWeight:isToday?700:400,
                  color:isToday?"#0a0c14":P.text}}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:16,padding:"8px 14px",borderBottom:`1px solid ${P.border}`,flexWrap:"wrap"}}>
          {[['#16a34a','3 libere'],['#d97706','2 libere'],['#c2410c','1 libera'],['#b91c1c','0 libere']].map(([c,l])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:13}}>
              <div style={{width:12,height:12,borderRadius:3,background:c}}/>
              <span style={{color:P.muted}}>{l}</span>
            </div>
          ))}
        </div>
        <div style={{overflowY:"auto",maxHeight:520}}>
          <div style={{display:"flex"}}>
            <div style={{width:52,flexShrink:0}}>
              {HOURS.map(h=>(
                <div key={h} style={{height:H,display:"flex",alignItems:"flex-start",
                  justifyContent:"flex-end",paddingRight:6,paddingTop:2,
                  fontSize:15,color:P.secondary,borderBottom:`1px solid ${P.border}`}}>
                  {h}:00
                </div>
              ))}
            </div>
            {days.map(d=>{
              const events = getEventsForDate(d);
              const isToday = d.toDateString()===today.toDateString();
              return (
                <div key={d.toString()} style={{flex:1,borderLeft:`1px solid ${P.border}`,
                  background:isToday?`${P.gold}04`:"transparent"}}>
                  {HOURS.map(h=>{
                    const count = countInSlot(events, h);
                    const libere = Math.max(0, AULE_TOTALI - count);
                    const col = auleColor(libere);
                    const isCurrentHour = isToday && today.getHours()===h;
                    const pct = isCurrentHour ? (today.getMinutes()/60)*100 : 0;
                    const lineH = (2/H)*100;
                    const endPct = Math.min(pct+lineH, 100);
                    const bg = isCurrentHour
                      ? `linear-gradient(to bottom, ${col}44 ${pct}%, ${P.red} ${pct}%, ${P.red} ${endPct}%, ${col}44 ${endPct}%)`
                      : `${col}44`;
                    return (
                      <div key={h} style={{height:H,
                        borderBottom:`1px solid ${P.border}`,
                        background:bg,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        transition:"background .2s"}}>
                        <div style={{width:28,height:28,borderRadius:"50%",
                          background:col,display:"flex",alignItems:"center",
                          justifyContent:"center",fontSize:15,fontWeight:800,color:"#fff",
                          boxShadow:"0 2px 6px rgba(0,0,0,0.45)"}}>
                          {libere}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── RENDER ───────────────────────────────────────────────────────────────────
  const titleStr = localView==='week'
    ? (()=>{
        const ws=getWeekStart(localDate);const we=new Date(ws);we.setDate(ws.getDate()+5);
        const pad=n=>String(n).padStart(2,'0');
        return isNarrow
          ? `${ws.getDate()}/${pad(ws.getMonth()+1)} – ${we.getDate()}/${pad(we.getMonth()+1)}`
          : `${ws.getDate()} ${MONTHS_IT_CAL[ws.getMonth()]} – ${we.getDate()} ${MONTHS_IT_CAL[we.getMonth()]} ${we.getFullYear()}`;
      })()
    : localView==='month'
    ? `${MONTHS_IT_CAL[localDate.getMonth()]} ${localDate.getFullYear()}`
    : `${DAYS_IT_CAL[localDate.getDay()]} ${localDate.getDate()} ${MONTHS_IT_CAL[localDate.getMonth()]} ${localDate.getFullYear()}`;

  return (
    <div style={{animation:"fi .35s ease",overflowX:"hidden"}}>

      {/* ── HEADER MAESTRO (mobile semplificato) ── */}
      {isMaestroRole ? (
        <div style={{marginBottom:14}}>
          {/* Riga 1: titolo + Aule + +Lezione */}
          <div style={{display:"flex",alignItems:"center",marginBottom:12,gap:8}}>
            <div style={{flex:"1 1 0",minWidth:0,overflow:"hidden"}}>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,
                color:P.muted,marginBottom:4,textTransform:"uppercase"}}>LE MIE LEZIONI</div>
              <div style={{fontSize:20,fontWeight:700,lineHeight:1.1,whiteSpace:"nowrap",
                overflow:"hidden",textOverflow:"ellipsis"}}>{titleStr}</div>
            </div>
            <div style={{display:"flex",gap:8,flexShrink:0}}>
              <button onClick={()=>{ const next=!showAule; setShowAule(next); if(next) setLocalView('week'); }}
                style={{background:showAule?P.gold:P.card,color:showAule?"#0a0c14":P.secondary,
                  border:`1px solid ${showAule?P.gold:P.border}`,padding:"11px 14px",
                  borderRadius:12,cursor:"pointer",fontWeight:600,fontSize:15,
                  minHeight:48,whiteSpace:"nowrap",transition:"all .15s"}}>
                🏠 Aule
              </button>
              <button onClick={()=>openNew(fmtDate(localDate),15)}
                style={{background:P.gold,color:"#fff",border:"none",padding:"11px 20px",
                  borderRadius:12,cursor:"pointer",fontWeight:700,fontSize:16,
                  minHeight:48,whiteSpace:"nowrap"}}>
                + Lezione
              </button>
            </div>
          </div>
          {/* Riga 2: navigazione + vista */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
            <div style={{display:"flex",gap:5,alignItems:"center"}}>
              <button onClick={()=>navigate(-1)}
                style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,
                  width:44,height:44,borderRadius:10,cursor:"pointer",fontSize:18,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
              <button onClick={()=>setLocalDate(new Date())}
                style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,
                  padding:"0 12px",height:44,borderRadius:10,cursor:"pointer",fontSize:15,fontWeight:500}}>
                Oggi
              </button>
              <button onClick={()=>navigate(1)}
                style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,
                  width:44,height:44,borderRadius:10,cursor:"pointer",fontSize:18,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
            </div>
            <div style={{display:"flex",background:P.bg,border:`1px solid ${P.border}`,borderRadius:10,padding:3,gap:1}}>
              {(showAule ? [['week','Sett.']] : [['week','Sett.'],['month','Mese'],['day','Giorno']]).map(([v,l])=>(
                <button key={v} onClick={()=>setLocalView(v)} style={{
                  padding:"0 10px",height:38,borderRadius:7,border:"none",cursor:"pointer",fontSize:14,
                  background:localView===v?P.gold:"transparent",
                  color:localView===v?"#0a0c14":P.secondary,fontWeight:localView===v?600:400,
                  transition:"all .15s"}}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
      <>
      {/* ── HEADER STANDARD ── */}
      <div style={{marginBottom:14}}>
        {/* Riga 1: Titolo + Cal/Aule + +Lezione */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,letterSpacing:3,color:P.muted,marginBottom:4,fontWeight:400,textTransform:"uppercase"}}>ORARIO LEZIONI</div>
            <div style={{fontSize:isNarrow?20:26,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{titleStr}</div>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
            <div style={{display:"flex",background:P.bg,border:`1px solid ${P.border}`,borderRadius:10,padding:3,gap:2}}>
              {[['cal','Cal.'],['aule','Aule']].map(([v,l])=>(
                <button key={v} onClick={()=>{ setShowAule(v==='aule'); if(v==='aule') setLocalView('week'); }} style={{
                  padding:"7px 12px",borderRadius:8,border:"none",cursor:"pointer",fontSize:14,
                  background:(v==='aule')===showAule?P.gold:"transparent",
                  color:(v==='aule')===showAule?"#0a0c14":P.secondary,
                  fontWeight:(v==='aule')===showAule?600:400,
                  transition:"all .15s"}}>
                  {l}
                </button>
              ))}
            </div>
            <button onClick={()=>openNew(fmtDate(localDate),15)}
              style={{background:P.gold,color:"#fff",border:"none",padding:"8px 14px",
                borderRadius:10,cursor:"pointer",fontWeight:600,fontSize:15,minHeight:38}}>
              + Lezione
            </button>
          </div>
        </div>
        {/* Riga 2: ‹ Oggi › + S/M/G */}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>navigate(-1)} style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,padding:"8px 14px",borderRadius:10,cursor:"pointer",fontSize:17,minWidth:38,minHeight:38}}>‹</button>
          <button onClick={()=>setLocalDate(new Date())} style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,padding:"8px 12px",borderRadius:10,cursor:"pointer",fontSize:15,minHeight:38}}>Oggi</button>
          <button onClick={()=>navigate(1)} style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,padding:"8px 14px",borderRadius:10,cursor:"pointer",fontSize:17,minWidth:38,minHeight:38}}>›</button>
          <div style={{marginLeft:"auto",display:"flex",background:P.bg,border:`1px solid ${P.border}`,borderRadius:10,padding:3,gap:2}}>
            {(showAule ? [['week', isNarrow ? 'S' : 'Settimana']] : [['week', isNarrow ? 'S' : 'Settimana'],['month', isNarrow ? 'M' : 'Mese'],['day', isNarrow ? 'G' : 'Giorno']]).map(([v,l])=>(
              <button key={v} onClick={()=>setLocalView(v)} style={{
                padding: isNarrow ? "7px 14px" : "7px 13px",borderRadius:8,border:"none",cursor:"pointer",fontSize:15,minHeight:38,
                background:localView===v?P.gold:"transparent",
                color:localView===v?"#0a0c14":P.secondary,fontWeight:localView===v?600:400,
                transition:"all .15s"}}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      </>
      )}


      {/* Dialog conferma sposta ricorrente */}
      {moveConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.65)",zIndex:300,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:16,
            padding:28,width:320,maxWidth:"90vw"}}>
            <div style={{fontSize:16,fontWeight:600,color:P.text,marginBottom:6}}>Sposta evento ricorrente</div>
            <div style={{fontSize:14,color:P.secondary,marginBottom:20}}>Vuoi spostare solo questa occorrenza o tutte le future?</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button onClick={moveOnlyOccurrence}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.blue}55`,
                  background:`${P.blue}18`,color:P.blue,cursor:"pointer",fontSize:15,textAlign:"left",fontWeight:500}}>
                Solo questa occorrenza
              </button>
              <button onClick={moveAllOccurrences}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.gold}55`,
                  background:`${P.gold}18`,color:P.gold,cursor:"pointer",fontSize:15,textAlign:"left",fontWeight:500}}>
                Tutte le occorrenze
              </button>
              <button onClick={()=>setMoveConfirm(null)}
                style={{padding:"10px 14px",borderRadius:9,border:`1px solid ${P.border}`,
                  background:"transparent",color:P.muted,cursor:"pointer",fontSize:15,marginTop:4}}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog conferma modifica ricorrente */}
      {editConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.65)",zIndex:300,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:16,
            padding:28,width:320,maxWidth:"90vw"}}>
            <div style={{fontSize:16,fontWeight:600,color:P.text,marginBottom:6}}>Modifica evento ricorrente</div>
            <div style={{fontSize:14,color:P.secondary,marginBottom:20}}>Vuoi modificare solo questa occorrenza o tutte quelle da oggi in avanti?</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button onClick={editOnlyOccurrence}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.blue}55`,
                  background:`${P.blue}18`,color:P.blue,cursor:"pointer",fontSize:15,textAlign:"left",fontWeight:500}}>
                Solo questa occorrenza
              </button>
              <button onClick={editAllOccurrences}
                style={{padding:"12px 14px",borderRadius:9,border:`1px solid ${P.gold}55`,
                  background:`${P.gold}18`,color:P.gold,cursor:"pointer",fontSize:15,textAlign:"left",fontWeight:500}}>
                Questa e tutte le future
              </button>
              <button onClick={()=>setEditConfirm(null)}
                style={{padding:"10px 14px",borderRadius:9,border:`1px solid ${P.border}`,
                  background:"transparent",color:P.muted,cursor:"pointer",fontSize:15,marginTop:4}}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner move mode */}
      {moveMode && (
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:100,
          background:P.blue,padding:"12px 20px",display:"flex",alignItems:"center",
          justifyContent:"space-between",gap:12}}>
          <span style={{color:"#fff",fontSize:15,fontWeight:600}}>
            Tocca uno slot per spostare l'evento
          </span>
          <button onClick={()=>setMoveMode(null)}
            style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,
              color:"#fff",padding:"6px 14px",cursor:"pointer",fontSize:15,fontWeight:600}}>
            Annulla
          </button>
        </div>
      )}

      {!showAule && localView==='week' && <WeekView/>}
      {!showAule && localView==='month' && <MonthView/>}
      {!showAule && localView==='day' && <DayView/>}
      {showAule && <AuleView/>}

      <EventModal/>
    </div>
  );
}
// Thu Apr  9 23:03:07 CEST 2026
